import { inject, Injectable } from '@angular/core';
import { ConfirmComponent, DialogService } from '@degreed/apollo';
import { TranslateService } from '@ngx-translate/core';
import { catchError, EMPTY, Observable } from 'rxjs';
import { AutomationCloneModalComponent } from '../automation-clone-modal/automation-clone-modal.component';
import { AutomationsBulkEnableModalComponent } from '../automations-bulk-enable-modal/automations-bulk-enable-modal.component';
import {
  AutomationCloneDialogData,
  BulkEnableDialogData,
  BusinessRule,
} from '../model';

@Injectable({
  providedIn: 'root',
})
export class AutomationsModalService {
  private readonly translate = inject(TranslateService);
  private readonly dialogService = inject(DialogService);

  private i18n = this.translate.instant([
    'Automation_DeleteAutomations',
    'Core_Delete',
    'BusinessRules_ToggleActiveModalHeader_ToDisable',
    'BusinessRules_ToggleActiveModalConfirm_ToDisable',
    'BusinessRules_ToggleActiveModalHeader_ToEnable',
    'BusinessRules_ToggleActiveModalConfirm_ToEnable',
  ]);

  public showNoEnableableAutomationsModal(): Observable<void> {
    return this.confirm$({
      title: this.translate.instant(
        'Automation_BulkEnable_NoValidAutomationsHeader'
      ),
      content: this.translate.instant(
        'Automation_BulkEnable_NoValidAutomationsBody'
      ),
      primaryBtnLabel: this.translate.instant('Core_Okay'),
    });
  }

  public showDisableModal(name: string): Observable<void> {
    return this.confirm$({
      title: this.i18n.BusinessRules_ToggleActiveModalHeader_ToDisable,
      content: this.translate.instant(
        'BusinessRules_ToggleActiveModalBody_ToDisable_Format',
        { name }
      ),
      primaryBtnLabel:
        this.i18n.BusinessRules_ToggleActiveModalConfirm_ToDisable,
    });
  }

  public showEnableModal(name: string): Observable<void> {
    return this.confirm$({
      title: this.i18n.BusinessRules_ToggleActiveModalHeader_ToEnable,
      content: this.translate.instant(
        'BusinessRules_ToggleActiveModalBody_ToEnable_Format',
        { name }
      ),
      primaryBtnLabel:
        this.i18n.BusinessRules_ToggleActiveModalConfirm_ToEnable,
    });
  }

  public showDeleteModal(name: string): Observable<void> {
    return this.confirm$({
      title: this.i18n.Core_Delete,
      content: this.translate.instant('BusinessRules_DeleteModalBody_Format', {
        name,
      }),
      primaryBtnLabel: this.i18n.Core_Delete,
      theme: 'destructive',
    });
  }

  public showCloneModal(
    rule: BusinessRule,
    orgId: number
  ): Observable<BusinessRule> {
    const dialogRef = this.dialogService.show<AutomationCloneDialogData>(
      AutomationCloneModalComponent,
      { automation: rule, orgId }
    );

    return new Observable<BusinessRule>((subscriber) => {
      dialogRef.closed.subscribe((result) => {
        if (result) {
          subscriber.next(result as BusinessRule);
        }
        subscriber.complete();
      });
    });
  }

  public showBulkDeleteModal(
    selectedAutomations: BusinessRule[]
  ): Observable<void> {
    const content = this.translate.instant(
      'Automation_AreYouSure_DeleteAutomations_Format',
      { count: selectedAutomations?.length }
    );

    return this.confirm$({
      title: this.i18n.Automation_DeleteAutomations,
      content,
      primaryBtnLabel: this.i18n.Core_Delete,
      theme: 'destructive',
      mode: 'input',
      inputMatch: 'DELETE',
    }).pipe(catchError(() => EMPTY));
  }

  public showBulkEnableModal(
    selectedAutomations: BusinessRule[],
    enable: boolean,
    scopeSelectionEnabled: boolean
  ): Observable<void> {
    const dialogRef = this.dialogService.show<BulkEnableDialogData>(
      AutomationsBulkEnableModalComponent,
      { selectedAutomations, enable, scopeSelectionEnabled }
    );

    return new Observable<void>((subscriber) => {
      dialogRef.closed.subscribe((result) => {
        if (result) {
          subscriber.next();
        }
        subscriber.complete();
      });
    });
  }

  /**
   * Helper: show an Apollo ConfirmComponent and return an Observable that
   * emits once on confirm, or completes without emitting on cancel/dismiss.
   */
  private confirm$(options: {
    title: string;
    content: string;
    primaryBtnLabel: string;
    theme?: string;
    mode?: string;
    inputMatch?: string;
  }): Observable<void> {
    return new Observable<void>((subscriber) => {
      this.dialogService.show(ConfirmComponent, {
        ...options,
        onClose: (confirmed: boolean) => {
          if (confirmed) {
            subscriber.next();
          }
          subscriber.complete();
        },
      });
    });
  }
}
